import { create } from 'zustand';
import type { SpaceInfo, Surface, SurfaceType } from '../types';
import { parseSurfaceLabel } from '../lib/utils';

interface SpaceState {
  // Space data
  spaceId: string | null;
  spaceInfo: SpaceInfo | null;
  isLoading: boolean;
  error: string | null;

  // Images
  originalImage: string | null;
  referenceImage: string | null;

  // Surfaces
  surfaces: Surface[];
  selectedSurface: Surface | null;

  // UI state
  activeTab: 'surfaces' | 'textures' | 'shading' | 'preview';

  // Actions
  setSpaceId: (id: string) => void;
  setSpaceInfo: (info: SpaceInfo) => void;
  setOriginalImage: (image: string) => void;
  setReferenceImage: (image: string) => void;
  setSurfaces: (surfaceLabels: string[]) => void;
  setSelectedSurface: (surface: Surface | null) => void;
  updateSurfaceMarker: (type: SurfaceType, index: number, x: number, y: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setActiveTab: (tab: 'surfaces' | 'textures' | 'shading' | 'preview') => void;
  reset: () => void;
}

const initialState = {
  spaceId: null,
  spaceInfo: null,
  isLoading: false,
  error: null,
  originalImage: null,
  referenceImage: null,
  surfaces: [],
  selectedSurface: null,
  activeTab: 'surfaces' as const,
};

export const useSpaceStore = create<SpaceState>((set, get) => ({
  ...initialState,

  setSpaceId: (id) => set({ spaceId: id }),

  setSpaceInfo: (info) => set({ spaceInfo: info }),

  setOriginalImage: (image) => set({ originalImage: image }),

  setReferenceImage: (image) => set({ referenceImage: image }),

  setSurfaces: (surfaceLabels) => {
    const surfaces: Surface[] = surfaceLabels.map((label) => {
      const { type, index } = parseSurfaceLabel(label);
      return {
        type: type as SurfaceType,
        index,
        label,
      };
    });
    set({ surfaces });

    // Auto-select first surface if none selected
    const { selectedSurface } = get();
    if (!selectedSurface && surfaces.length > 0) {
      set({ selectedSurface: surfaces[0] });
    }
  },

  setSelectedSurface: (surface) => set({ selectedSurface: surface }),

  updateSurfaceMarker: (type, index, x, y) => {
    const { surfaces, spaceInfo } = get();
    const updatedSurfaces = surfaces.map((s) => {
      if (s.type === type && s.index === index) {
        return { ...s, marker: { id: index, x, y } };
      }
      return s;
    });
    set({ surfaces: updatedSurfaces });

    // Also update spaceInfo if available
    if (spaceInfo) {
      const updatedMarkers = { ...spaceInfo.surfaceMarkers };
      const typeKey = type as keyof typeof updatedMarkers;
      if (updatedMarkers[typeKey]) {
        updatedMarkers[typeKey] = updatedMarkers[typeKey]!.map((m) =>
          m.id === index ? { ...m, x, y } : m
        );
      }
      set({ spaceInfo: { ...spaceInfo, surfaceMarkers: updatedMarkers } });
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  reset: () => set(initialState),
}));
